<template>
    <Layout>
        <div class="event-schedule-page">

            <!-- Hero Section: Banner chương trình lớn đầu trang -->
            <section class="hero-section">
                <div class="leaf-pattern-bg"></div>
                <div class="container text-center hero-content-box">
                    <div class="badge-tag-eco">
                        <span class="isax isax-teacher icon-eco"></span>
                        {{ $t('schedule_page.green_journey') || 'HÀNH TRÌNH XANH 2026' }}
                    </div>
                    <h1 class="hero-main-title">
                        {{ $t('schedule_page.hero_title') || 'CHƯƠNG TRÌNH HOẠT ĐỘNG LỄ HỘI TRÁI CÂY ĐỒNG THÁP 2026' }}
                    </h1>
                    <p class="hero-sub-desc">
                        {{ $t('schedule_page.hero_desc') }}
                    </p>
                </div>
            </section>

            <!-- Tabbed Navigation: Điều hướng chuyển đổi phân loại sự kiện -->
            <section class="sticky-tabs-nav">
                <div class="container">
                    <div class="tabs-scroll-container">
                        <button v-for="tab in tabs" :key="tab.id" type="button"
                            :class="['tab-btn-custom', activeTab === tab.id ? 'tab-active' : '']"
                            @click="switchTab(tab.id)">
                            <span :class="['isax', tab.icon, 'tab-icon-left']"></span>
                            {{ tab.name }}
                        </button>
                    </div>
                </div>
            </section>

            <!-- Content Area: Danh sách các sự kiện được kết xuất dạng Grid 2 cột -->
            <section class="container py-5 content-activities-section">
                <div v-if="filteredActivities.length === 0" class="empty-activities-state">
                    <span class="isax isax-calendar-remove empty-icon"></span>
                    <p>{{ $t('schedule_page.empty_state') }}</p>
                </div>

                <div v-else class="row">
                    <!-- Lặp danh sách hoạt động động -->
                    <div v-for="activity in filteredActivities" :key="activity.id" class="col-lg-6 mb-4">
                        <div class="activity-bento-card">
                            <!-- Header Card: Thời gian và Tiêu chí quan trọng -->
                            <div class="card-header-flex">
                                <div class="card-date-meta">
                                    <div class="date-badge-box">
                                        <span class="date-month">{{ activity.month }}</span>
                                        <span class="date-day">{{ activity.day }}</span>
                                    </div>
                                    <div class="time-title-meta">
                                        <span class="tag-category-text">{{ activity.categoryName }}</span>
                                        <div class="time-text-row">
                                            <span class="isax isax-clock icon-clock-sm"></span>
                                            <span>{{ activity.timeRange }}</span>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="activity.isImportant" class="badge-priority-high">
                                    {{ $t('schedule_page.important') || 'Quan trọng' }}
                                </span>
                            </div>

                            <!-- Tên sự kiện -->
                            <h3 class="activity-card-title">
                                {{ activity.title }}
                            </h3>

                            <!-- Địa điểm và chủ trì -->
                            <div class="activity-location-group">
                                <div class="location-item-row" v-if="activity.location">
                                    <span class="isax isax-location icon-spec"></span>
                                    <div class="meta-desc">
                                        <p class="meta-label">{{ $t('schedule_page.location') || 'Địa điểm' }}</p>
                                        <p class="meta-value">{{ activity.location }}</p>
                                    </div>
                                </div>

                                <div class="location-item-row" v-if="activity.organizer">
                                    <span class="isax isax-profile-2user icon-spec"></span>
                                    <div class="meta-desc">
                                        <p class="meta-label">{{ $t('schedule_page.organizer') }}</p>
                                        <p class="meta-value">{{ activity.organizer }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Nút Thao tác & Phối hợp (đặt trước collapse để không bị đẩy xuống) -->
                            <div class="card-actions-row mt-auto">
                                <button v-if="hasDetails(activity)" type="button" class="btn-toggle-expand me-2"
                                    @click="activity.isExpanded = !activity.isExpanded">
                                    <span
                                        :class="['isax', activity.isExpanded ? 'isax-arrow-up-1' : 'isax-arrow-down-1', '']"></span>
                                    {{ activity.isExpanded ? ($t('home.schedule.hide_details') || 'Thu gọn') :
                                        ($t('home.schedule.show_details') || 'Xem chi tiết') }}
                                </button>

                                <div v-if="activity.partner"
                                    class="partner-cooperate-text ms-auto ps-2 border-start d-flex align-items-center">
                                    {{ $t('schedule_page.partner') || 'Phối hợp' }}: {{ activity.partner }}
                                </div>
                            </div>

                            <!-- Chi tiết có thể mở rộng/thu gọn -->
                            <b-collapse :visible="activity.isExpanded">
                                <div class="expanded-details-box mt-3 pt-3 border-top">
                                    <!-- Quy mô, Ý tưởng -->
                                    <div class="row g-2 mb-3 text-left" v-if="activity.quyMo || activity.yTuong">
                                        <div v-if="activity.quyMo" class="col-md-6">
                                            <div class="meta-desc">
                                                <p class="meta-label">{{ $t('schedule_page.scale') || 'Quy mô' }}</p>
                                                <p class="meta-value text-success fw-bold">{{ activity.quyMo }}</p>
                                            </div>
                                        </div>
                                        <div v-if="activity.yTuong" class="col-md-6">
                                            <div class="meta-desc">
                                                <p class="meta-label">{{ $t('schedule_page.concept') || 'Ý tưởng' }}</p>
                                                <p class="meta-value">{{ activity.yTuong }}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Hình thức (Render HTML) -->
                                    <div class="location-item-row mb-3 text-left" v-if="activity.hinhThuc">
                                        <div class="meta-desc w-100">
                                            <p class="meta-label">{{ $t('schedule_page.format') || 'Hình thức' }}</p>
                                            <div class="ck-content mt-1 p-3 border rounded bg-white text-dark font-size-13 text-left w-100"
                                                v-html="activity.hinhThuc"></div>
                                        </div>
                                    </div>

                                    <!-- Nội dung chi tiết hoạt động -->
                                    <div class="activity-details-prose"
                                        v-if="activity.contents && activity.contents.length > 0">
                                        <p class="prose-title">
                                            <span class="isax isax-document-text-1"></span>
                                            {{ $t('schedule_page.details') || 'Nội dung hoạt động chi tiết' }}
                                        </p>
                                        <ul class="prose-list">
                                            <li v-for="(content, idx) in activity.contents" :key="idx"
                                                class="prose-item">
                                                <span class="isax isax-tick-circle item-tick"></span>
                                                {{ content }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </b-collapse>
                        </div>
                    </div>
                </div>

                <!-- concept Xem thêm hoạt động -->
                <!-- <div class="text-center mt-5" v-if="filteredActivities.length > 0">
                    <button type="button" class="btn-load-more" @click="loadMoreActivities">
                        <span class="isax isax-arrow-down-1"></span>
                        Xem thêm chương trình hoạt động
                    </button>
                </div> -->
            </section>

            <!-- Newsletter / Registration Section -->
            <!-- <section class="newsletter-section">
                <div class="newsletter-backdrop"></div>
                <div class="container position-relative">
                    <div class="row align-items-center">
                        <div class="col-lg-7 text-left pr-lg-5 mb-4 mb-lg-0">
                            <h2 class="newsletter-title">Bạn không muốn bỏ lỡ bất kỳ hoạt động nào?</h2>
                            <p class="newsletter-desc">Đăng ký nhận thông báo và tài liệu hướng dẫn tham gia lễ hội trực
                                tiếp qua hòm thư điện tử.</p>
                        </div>
                        <div class="col-lg-5">
                            <form @submit.prevent="handleSubscribe" class="newsletter-form">
                                <input v-model="subscribeEmail" type="email" class="newsletter-input"
                                    placeholder="Nhập email nhận tin của bạn..." required />
                                <button type="submit" class="btn-newsletter-submit">
                                    ĐĂNG KÝ NGAY
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section> -->

        </div>
    </Layout>
</template>

<script>
import Layout from "@/layouts/layout.vue";
import moment from "moment";

export default {
    name: "EventSchedule",
    components: {
        Layout
    },
    created() {
        this.fetchActivities();
    },
    data() {
        return {
            activeTab: "pre", // Mặc định mở rộng tab "Hoạt động chính"
            subscribeEmail: "",
            // Hệ thống danh sách hoạt động bento bám sát dữ liệu thực tế
            activities: []
        };
    },
    computed: {
        filteredActivities() {
            // Lọc danh sách hoạt động dựa trên Tab hiện tại
            return this.activities.filter(act => act.tabType === this.activeTab);
        },
        tabs() {
            return [
                { id: "pre", name: this.$t('home.tabs.chinh') || "Hoạt động chính", icon: "isax-status-up" },
                { id: "ongoing", name: this.$t('home.tabs.xuyenSuot') || "Hoạt động xuyên suốt", icon: "isax-calendar-1" },
                { id: "hourly", name: this.$t('home.tabs.khungGio') || "Lịch theo khung giờ", icon: "isax-clock" },
                { id: "response", name: this.$t('home.tabs.huongUng') || "Hoạt động hưởng ứng", icon: "isax-award" }
            ];
        }
    },
    methods: {
        hasDetails(activity) {
            return (activity.quyMo && activity.quyMo.trim() !== "") ||
                (activity.yTuong && activity.yTuong.trim() !== "") ||
                (activity.hinhThuc && activity.hinhThuc.trim() !== "") ||
                (activity.contents && activity.contents.length > 0);
        },
        switchTab(tabId) {
            this.activeTab = tabId;
        },
        async fetchActivities() {
            try {
                await this.$store?.dispatch('loadingStore/show');
                const res = await this.$store.dispatch("profession/chuongTrinh/chuongTrinhStore/getAll");
                if (res?.data) {
                    let mappedActivities = [];

                    const normalizeUnits = (val) => {
                        if (!val) return "";
                        if (Array.isArray(val)) {
                            return val.map(d => d.name || d).join(', ');
                        }
                        return val;
                    };

                    const getMonthName = (dateStr) => {
                        const currentLocale = this.$i18n ? this.$i18n.locale : "vi";
                        if (!dateStr) return currentLocale === 'en' ? 'Jun' : 'Thg 6';
                        if (currentLocale === 'en') {
                            return moment(dateStr).format("MMM");
                        }
                        return "Thg " + moment(dateStr).format("M");
                    };

                    const getDayStr = (dateStr) => {
                        if (!dateStr) return "05";
                        return moment(dateStr).format("DD");
                    };

                    const mapActivityItem = (e, idx, tabType, defaultCategory, isImportantDefault = false) => {
                        const currentLocale = this.$i18n ? this.$i18n.locale : "vi";
                        const getLangVal = (vietVal, engVal) => {
                            return currentLocale === 'en' ? (engVal || vietVal || "") : (vietVal || "");
                        };
                        const getLangCategoryName = () => {
                            if (currentLocale === 'en') {
                                return e.thamMuuEn || e.thamMuu || defaultCategory;
                            }
                            return e.thamMuu || defaultCategory;
                        };
                        return {
                            id: e._id || `${tabType}-${idx}-${Date.now()}-${Math.random()}`,
                            tabType: tabType,
                            month: getMonthName(e.ngayBatDau),
                            day: getDayStr(e.ngayBatDau),
                            categoryName: getLangCategoryName(),
                            timeRange: e.gioBatDau ? (e.gioBatDau + (e.gioKetThuc ? ' - ' + e.gioKetThuc : '')) : (tabType === 'ongoing' ? (this.$t('schedule_page.ongoing_duration') || 'Suốt Lễ hội') : (this.$t('schedule_page.all_day') || 'Cả ngày')),
                            isImportant: isImportantDefault,
                            title: getLangVal(e.name || e.tenHoatDong, e.nameEn || e.tenHoatDongEn),
                            location: getLangVal(e.diaChi || e.diaDiem, e.diaChiEn || e.diaDiemEn),
                            organizer: getLangVal(e.chuTri, e.chuTriEn),
                            contents: e.noiDungs ? e.noiDungs.map(nd => currentLocale === 'en' ? (nd.textEn || nd.text) : nd.text).filter(Boolean) : [],
                            hasAttachment: !!(e.file && (e.file.fileId || e.file.path)),
                            file: e.file,
                            partner: currentLocale === 'en' ? (normalizeUnits(e.phoiHopEn) || normalizeUnits(e.phoiHop)) : normalizeUnits(e.phoiHop),
                            quyMo: getLangVal(e.quyMo || e.quiMo, e.quyMoEn || e.quiMoEn),
                            yTuong: getLangVal(e.yTuong, e.yTuongEn),
                            hinhThuc: getLangVal(e.hinhThuc, e.hinhThucEn),
                            isExpanded: false,
                        };
                    };

                    res.data.forEach((cat) => {
                        // cat.truocLeHoi -> pre
                        if (cat.truocLeHoi) {
                            cat.truocLeHoi.forEach((e, idx) => {
                                mappedActivities.push(mapActivityItem(e, idx, "pre", this.$t('home.schedule.advise') || "Tham mưu"));
                            });
                        }

                        // cat.hoatDongXuyenSuot -> ongoing
                        if (cat.hoatDongXuyenSuot) {
                            cat.hoatDongXuyenSuot.forEach((e, idx) => {
                                mappedActivities.push(mapActivityItem(e, idx, "ongoing", this.$t('home.tabs.xuyenSuot') || "Xuyên suốt"));
                            });
                        }

                        // cat.hoatDongChinh -> ongoing
                        const mainAct = cat.truocLeHoi || cat.hoatDongChinh;
                        if (mainAct && mainAct !== cat.truocLeHoi) {
                            mainAct.forEach((e, idx) => {
                                mappedActivities.push(mapActivityItem(e, idx, "ongoing", this.$t('home.schedule.focus') || "Trọng tâm", true));
                            });
                        }

                        // cat.hoatDongTheoKhungSo -> hourly
                        if (cat.hoatDongTheoKhungSo) {
                            cat.hoatDongTheoKhungSo.forEach((e, idx) => {
                                mappedActivities.push(mapActivityItem(e, idx, "hourly", this.$t('home.tabs.khungGio') || "Theo khung giờ"));
                            });
                        }

                        // cat.hoatDongHuongUng -> response
                        if (cat.hoatDongHuongUng) {
                            cat.hoatDongHuongUng.forEach((e, idx) => {
                                mappedActivities.push(mapActivityItem(e, idx, "response", this.$t('home.tabs.huongUng') || "Hưởng ứng"));
                            });
                        }
                    });

                    this.activities = mappedActivities;
                }
            } catch (error) {
                console.error(this.$t('schedule_page.update_error') || "Lỗi khi tải danh sách hoạt động:", error);
            } finally {
                await this.$store?.dispatch('loadingStore/hide');
            }
        },
        downloadDocument(activity) {
            if (activity.file && activity.file.fileId) {
                const apiView = (process.env.VUE_APP_API_URL || "") + "filesminio/view/";
                window.open(apiView + activity.file.fileId, "_blank");
            } else {
                this.$store?.dispatch("snackBarStore/addNotify", {
                    message: (this.$t('schedule_page.no_attachment') || 'Không tìm thấy tài liệu đính kèm cho hoạt động:') + ` "${activity.title}"`,
                    code: 400
                });
            }
        },
        loadMoreActivities() {
            this.$store?.dispatch("snackBarStore/addNotify", {
                message: this.$t('schedule_page.more_updates') || "Tính năng đang tiếp tục được cập nhật thêm lịch từ Ban tổ chức.",
                code: 0
            });
        },
        handleSubscribe() {
            if (!this.subscribeEmail) return;

            this.$store?.dispatch("snackBarStore/addNotify", {
                message: `Đăng ký thành công! Ban tổ chức sẽ gửi chương trình mới nhất qua email: ${this.subscribeEmail}`,
                code: 0
            });
            this.subscribeEmail = "";
        }
    }
};
</script>

<style scoped>
/* ======================== THỜI GIAN & CHỦ ĐỀ CHUNG ======================== */
.event-schedule-page {
    background-color: var(--background, #f7fbf0);
    color: var(--on-surface, #181d17);
    font-family: var(--font-body-standard), sans-serif;
    line-height: 1.6;
}

/* ======================== HERO SECTION ======================== */
.hero-section {
    position: relative;
    background-color: var(--surface-container-low, #f1f5eb);
    padding: 32px 16px;
    overflow: hidden;
}

@media (min-width: 768px) {
    .hero-section {
        padding: 80px 40px;
    }
}

.leaf-pattern-bg {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(var(--primary, #0d631b) 0.5px, transparent 0.5px);
    background-size: 24px 24px;
    opacity: 0.04;
}

.hero-content-box {
    position: relative;
    z-index: 5;
    text-align: center;
}

.badge-tag-eco {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: var(--primary-fixed, #a3f69c);
    color: var(--on-primary-fixed-variant, #005312);
    border-radius: 9999px;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.79rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 24px;
}

.icon-eco {
    font-size: 1rem;
}

.hero-main-title {
    font-family: var(--font-heading-admin), sans-serif;
    font-size: 2rem;
    font-weight: 800;
    color: var(--text-heading, #1b5e20);
    line-height: 1.25;
    max-width: 840px;
    margin: 0 auto 24px auto;
    text-transform: uppercase;
}

@media (min-width: 768px) {
    .hero-main-title {
        font-size: 2.86rem;
    }
}

.hero-sub-desc {
    font-family: var(--font-body-standard), sans-serif;
    font-size: 1.14rem;
    color: var(--on-surface-variant, #40493d);
    max-width: 640px;
    margin: 0 auto;
}

/* ======================== STICKY TABS NAVIGATION ======================== */
.sticky-tabs-nav {
    position: sticky;
    top: 64px;
    /* Tránh bị đè bởi HeaderNavBar (cao 64px) */
    background-color: #ffffff;
    border-bottom: 1px solid var(--outline-variant, #bfcaba);
    z-index: 40;
}

.sticky-tabs-nav .container {
    max-width: var(--container-max, 1280px);
}

.tabs-scroll-container {
    display: flex;
    overflow-x: auto;
    gap: 12px;
    padding: 16px 0;
    scrollbar-width: none;
    /* Ẩn scrollbar trên Firefox */
    -webkit-overflow-scrolling: touch;
}

.tabs-scroll-container::-webkit-scrollbar {
    display: none;
    /* Ẩn scrollbar trên Chrome, Safari */
}

.tab-btn-custom {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background-color: transparent;
    color: var(--on-surface-variant, #40493d);
    border: 1px solid transparent;
    border-radius: 12px;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.93rem;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    outline: none;
    transition: all 0.25s ease;
}

.tab-btn-custom:hover {
    background-color: var(--surface-container, #ebefe5);
    color: var(--primary, #0d631b);
}

.tab-btn-custom.tab-active {
    background-color: var(--primary-container, #2e7d32);
    color: #ffffff;
    border-color: var(--primary-container, #2e7d32);
    box-shadow: 0 4px 10px rgba(46, 125, 50, 0.15);
}

.tab-icon-left {
    font-size: 1.29rem;
}

/* ======================== DANH SÁCH BENTO CARDS ======================== */
.content-activities-section {
    max-width: var(--container-max, 1280px);
}

.empty-activities-state {
    text-align: center;
    padding: 80px 24px;
    color: var(--on-surface-variant, #40493d);
}

.empty-icon {
    font-size: 3.43rem;
    color: var(--outline, #707a6c);
    margin-bottom: 16px;
    display: block;
}

/* Thẻ hoạt động Bento Card */
.activity-bento-card {
    background-color: var(--surface-container-lowest, #ffffff);
    border: 1px solid var(--outline-variant, #bfcaba);
    border-radius: 16px;
    padding: 24px;
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
}

.activity-bento-card:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
    border-color: var(--primary, #0d631b);
}

/* Header bên trong Card */
.card-header-flex {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 24px;
}

.card-date-meta {
    display: flex;
    align-items: center;
    gap: 16px;
}

/* Khối ngày tháng đẹp */
.date-badge-box {
    width: 56px;
    height: 56px;
    background-color: var(--primary-container, #2e7d32);
    color: #ffffff;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.date-month {
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.80rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    line-height: 1;
}

.date-day {
    font-family: var(--font-heading-admin), sans-serif;
    font-size: 1.43rem;
    font-weight: 800;
    line-height: 1.1;
    margin-top: 2px;
}

.time-title-meta {
    text-align: left;
}

.tag-category-text {
    display: block;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.79rem;
    font-weight: 800;
    text-transform: uppercase;
    color: var(--primary, #0d631b);
    letter-spacing: 0.05em;
    margin-bottom: 4px;
}

.time-text-row {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.93rem;
    color: var(--on-surface-variant, #40493d);
}

.icon-clock-sm {
    font-size: 1.14rem;
}

/* Nhãn độ quan trọng */
.badge-priority-high {
    background-color: var(--secondary-fixed, #ffdcc2);
    color: var(--on-secondary-fixed-variant, #6d3a00);
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.71rem;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 9999px;
    white-space: nowrap;
}

/* Tiêu đề hoạt động */
.activity-card-title {
    font-family: var(--font-heading-admin), sans-serif;
    font-size: 1.29rem;
    color: var(--text-heading, #1b5e20);
    font-weight: 700;
    margin: 0 0 20px 0;
    text-align: left;
    line-height: 1.4;
}

/* Nhóm lề địa điểm */
.activity-location-group {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
    text-align: left;
}

.location-item-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.icon-spec {
    color: var(--primary, #0d631b);
    font-size: 1.43rem;
    margin-top: 2px;
}

.meta-desc {
    display: flex;
    flex-direction: column;
}

.meta-label {
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.79rem;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--on-surface-variant, #40493d);
    letter-spacing: 0.05em;
    margin: 0 0 2px 0;
}

.meta-value {
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.93rem;
    color: var(--on-surface, #181d17);
    margin: 0;
}

/* Nội dung hoạt động chi tiết */
.activity-details-prose {
    background-color: var(--surface-container-low, #f1f5eb);
    border-left: 4px solid var(--primary, #0d631b);
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    text-align: left;
}

.prose-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.86rem;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--on-surface, #181d17);
    margin: 0 0 12px 0;
}

.prose-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.prose-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.93rem;
    color: var(--on-surface-variant, #40493d);
}

.item-tick {
    color: var(--primary, #0d631b);
    font-size: 1.14rem;
    margin-top: 2px;
    flex-shrink: 0;
}

/* Dưới chân Card */
.card-actions-row {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid var(--outline-variant, #bfcaba);
    padding-top: 16px;
}

.btn-download-attachment {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background-color: transparent;
    border: 1px solid var(--primary, #0d631b);
    color: var(--primary, #0d631b);
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.86rem;
    font-weight: 700;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    outline: none;
}

.btn-download-attachment:hover {
    background-color: var(--primary, #0d631b);
    color: #ffffff;
}

.btn-toggle-expand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 16px;
    height: 36px;
    min-width: 120px;
    background-color: var(--primary-container, #e8f5e9);
    border: 1px solid transparent;
    color: white;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.86rem;
    font-weight: 700;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    outline: none;
}

.btn-toggle-expand:hover {
    background-color: var(--primary, #0d631b);
    color: #ffffff;
}

.partner-cooperate-text {
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.86rem;
    color: var(--on-surface-variant, #40493d);
}

.ck-content :deep(p) {
    margin-bottom: 0.5rem;
}

.ck-content :deep(ul),
.ck-content :deep(ol) {
    padding-left: 20px;
}

/* Nút tải thêm nông sản */
.btn-load-more {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 32px;
    background-color: var(--surface-container-high, #e5eadf);
    color: var(--on-surface-variant, #40493d);
    border: none;
    border-radius: 9999px;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.93rem;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.2s;
}

.btn-load-more:hover {
    background-color: var(--outline-variant, #bfcaba);
}

/* ======================== NEWSLETTER / ĐĂNG KÝ ======================== */
.newsletter-section {
    position: relative;
    background-color: var(--primary-container, #2e7d32);
    color: #ffffff;
    padding: 40px 16px;
    overflow: hidden;
    border-radius: 16px;
    margin: 40px 16px;
    max-width: var(--container-max, 1280px);
}

@media (min-width: 768px) {
    .newsletter-section {
        padding: 64px 48px;
        border-radius: 32px;
        margin: 64px auto;
    }
}

.newsletter-backdrop {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 30%;
    opacity: 0.15;
    pointer-events: none;
    background-image: radial-gradient(var(--secondary, #8f4e00) 0.5px, transparent 0.5px);
    background-size: 16px 16px;
}

.newsletter-title {
    font-family: var(--font-heading-admin), sans-serif;
    font-size: 1.71rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 12px;
}

@media (min-width: 768px) {
    .newsletter-title {
        font-size: 2.29rem;
    }
}

.newsletter-desc {
    font-family: var(--font-body-standard), sans-serif;
    font-size: 1.14rem;
    color: var(--primary-fixed-dim, #88d982);
    margin: 0;
}

.newsletter-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

@media (min-width: 576px) {
    .newsletter-form {
        flex-direction: row;
    }
}

.newsletter-input {
    flex-grow: 1;
    padding: 14px 20px;
    background-color: #ffffff;
    border: none;
    border-radius: 8px;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 1rem;
    color: var(--on-surface, #181d17);
    outline: none;
}

.btn-newsletter-submit {
    padding: 14px 28px;
    background-color: var(--secondary-container, #ff8f06);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    font-family: var(--font-heading-admin), sans-serif;
    font-weight: 700;
    font-size: 0.93rem;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: all 0.2s;
    outline: none;
    white-space: nowrap;
}

.btn-newsletter-submit:hover {
    background-color: var(--secondary, #8f4e00);
}

/* Responsive spacing for Mobile Viewport */
@media (max-width: 768px) {
    .content-activities-section {
        padding-top: 16px !important;
        /* Thu nhỏ khoảng cách giữa thanh chọn loại hoạt động và các item bên dưới */
        padding-bottom: 32px !important;
    }
}
</style>